<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="advSearch"
    @on-cancel="onCancel()"
    :okText="$t('Search')"
    :closeText="$t('Close')"
    maxWidth="65%"
  >
    <v-overlay :value="loading" absolute style="bottom: 60px; top: 50px">
      <v-progress-circular
        class="my-4"
        :size="60"
        width="5"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
    <v-tabs centered grow v-model="selectedTab">
      <v-tab>
        {{ $t("Search") }}
      </v-tab>
      <v-tab>
        {{ $t("ToGroup") }}
      </v-tab>
      <v-tab>
        {{ $t("Sort") }}
      </v-tab>
      <v-tab-item eager>
        <v-row class="pb-0 mb-0">
          <v-col cols="12" align-self="center">
            <v-alert
              v-if="showContentTypeWarning"
              dense
              outlined
              class="pt-1 pb-1"
              type="info"
              color="grey darken-1"
            >
            {{ $t("AdvancedSearchWarning") }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="auto" align-self="center">
            <base-multiselect
              :ref="contentTypesMultiselectKey"
              :items="userContentTypes"
              @on-selection-change="onContentTypeSelected"
              prependIcon="mdi-folder-open-outline"
            />
          </v-col>
          <v-col cols="12" sm="auto" align-self="center">
            <v-switch
              v-model="userSearch.searchAllVersions"
              :label="$t('SearchInAllVersions')"
            ></v-switch>
          </v-col>
          <v-col cols="12" sm="auto" align-self="center" class="ml-auto">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon text large color="error" @click="clear">
                  <v-icon>mdi-eraser</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Clear") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-divider></v-divider>
        <SearchCriteriaBuilder
          :ref="searchCriteriaBuilderKey"
          :contentTypeIDs="userSearch.contentTypeIDs"
          @on-loading-fields="loading = $event"
          @group-fields-updated="updateGroupFields($event)"
        />
        <UserSearchModal :ref="userSearchModalKey" />
      </v-tab-item>
      <v-tab-item eager>
          <GroupModal :ref="groupModalKey" :fieldList="filteredFields" :showContentTypeWarning="showContentTypeWarning" />
      </v-tab-item>
      <v-tab-item eager>
          <SortModal :ref="sortModalKey" :fieldList="filteredFields" :showContentTypeWarning="showContentTypeWarning" />
      </v-tab-item>
    </v-tabs>
    <template v-slot:actions>
      <div style="position: absolute">
        <v-menu bottom right v-if="userSavedSearches.length > 0">
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  color="warning"
                >
                  <v-icon large>mdi-database-edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Edit") }}</span>
            </v-tooltip>
          </template>
          <v-list :height="200" class="overflow-y-auto">
            <v-list-item
              v-for="(item, i) in userSavedSearches"
              :key="i"
              @click="onSearchEdit(item)"
            >
              <v-list-item-title>{{ item.FilterName }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" text small color="success" @click="onSearchSave">
              <v-icon large>mdi-content-save</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Save") }}</span>
        </v-tooltip>
        <v-tooltip top v-if="showDelete">
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" text small color="error" @click="onSearchDelete">
              <v-icon large>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Delete") }}</span>
        </v-tooltip>
      </div>
    </template>
  </base-modal>
</template>
<script>
import GroupModal from '@/components/search/results/GroupModal.vue';
import SortModal from '@/components/search/results/SortModal.vue';
import StandardFieldCode from '@/ecm/variables/StandardFieldCode';
import FieldType from '@/ecm/variables/FieldType';
import { validationMixin } from 'vuelidate';
import SearchType from '@/ecm/variables/SearchType';
import SearchCriteriaBuilder from '@/components/search/SearchCriteriaBuilder.vue';
import UserSearchModal from '@/components/search/advSearch/UserSearchModal.vue';
import UserSearch from '@/ecm/models/UserSearch';
import UserSearchService from '@/services/UserSearchService';
import Utils from '@/ecm/common/Utils.js';

export default {
    name: 'AdvSearchModal',
    components: {
        GroupModal,
        SortModal,
        SearchCriteriaBuilder,
        UserSearchModal,
    },
    props: {
        documentId: {
            type: Number,
            default: 0,
        },
    },
    created() {
        this.userSearch = new UserSearch();
        this.userSearch.contentTypeIDs = this.userContentTypes.map((x) => x.id);
    },
    data() {
        return {
            modal: {
                title: this.$t('AdvancedSearch'),
                key: 'advSearchModal',
            },
            selectedTab: 0,
            filteredFields: [],
            groupModalKey: 'groupModalKey',
            sortModalKey: 'sortModalKey',
            contentTypesMultiselectKey: 'ctAdvSearchMultiselectKey',
            searchCriteriaBuilderKey: 'searchCriteriaBuilderKey',
            userSearchModalKey: 'userSearchModalKey',
            userSearch: new UserSearch(),
            open: false,
            shouldLoadContentType: true,
            showContentTypeWarning: false,
            loading: false,
            groupFieldList: [],
        };
    },
    mixins: [validationMixin],
    validations: {},
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        groupModalInstance() {
            return this.$refs[this.groupModalKey];
        },
        sortModalInstance() {
            return this.$refs[this.sortModalKey];
        },
        modalTitle() {
            return (
                this.modal.title +
                (this.userSearch.name ? ` - ${this.userSearch.name}` : '')
            );
        },
        contentTypesMultiselectInstance() {
            return this.$refs[this.contentTypesMultiselectKey];
        },
        searchCriteriaBuilderInstance() {
            return this.$refs[this.searchCriteriaBuilderKey];
        },
        userSearchModalInstance() {
            return this.$refs[this.userSearchModalKey];
        },
        userContentTypes() {
            return this.$store.getters['account/userContentTypes'];
        },
        userSavedSearches() {
            return this.$store.getters['account/userSavedSearches'];
        },
        showDelete() {
            return this.userSearch.id > 0;
        },
    },
    methods: {
        onContentTypeSelected(contentTypeIDs) {
            this.showContentTypeWarning = contentTypeIDs.length > 1;
            if (this.shouldLoadContentType) this.userSearch.contentTypeIDs = contentTypeIDs;
            this.getFields();
        },
        onSearchEdit(item) {
            this.userSearch = new UserSearch(item);
            this.contentTypesMultiselectInstance.setSelectedItems(
                this.userSearch.contentTypeIDsIsLastVersion
            );
            this.$nextTick(() => {
                this.searchCriteriaBuilderInstance.edit(this.userSearch.conditionStr);
                this.groupModalInstance.setGroupObj(this.userSearch.groupStr);
                this.sortModalInstance.setSortObj(this.userSearch.sortStr);
            });
        },
        onSearchSave() {
            var condition = this.searchCriteriaBuilderInstance.getConditionStr();
            if (condition == '' || condition == 'INVALID') {
                return;
            }
            var group = this.groupModalInstance.getGroupStr();
            var sort = this.sortModalInstance.getSortStr();
            this.userSearch.conditionStr = condition;
            this.userSearch.groupStr = group;
            this.userSearch.sortStr = sort;
            this.userSearchModalInstance.edit(this.userSearch);
        },
        onSearchDelete() {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    UserSearchService.delete(this.userSearch.id).then(() => {
                        Utils.toast.success(this.$t('UserSearch') + ' ' + this.$t('DeletedSuccessfullyFem'));
                        this.userSearch = new UserSearch();
                        this.$store.dispatch('account/getUserSavedSearches');
                    });
                }
            });
        },
        updateGroupFields(fields) {
            this.groupFieldList = fields;
            this.getFields();
        },
        getFields() {
            var standardFields = this.$store.getters['account/searchStandardFields'];
            this.filteredFields = [];
            standardFields.forEach((sf) => {
                if (sf.Code !== StandardFieldCode.Fulltext) {
                    sf.Title = sf.DisplayText;
                    sf.IsVisible = true;
                    this.filteredFields.push(sf);
                }
            });
            if (this.userSearch.contentTypeIDs.length === 1) {
                this.groupFieldList.forEach((field) => {
                    const groupField = {
                        Code: field.Code,
                        Title: field.Name ? field.Name : field.Title,
                        Type: isNaN(field.Type) ? FieldType[field.Type] : field.Type,
                        IsVisible: true
                    };
                    this.filteredFields.push(groupField);
                });
            }
        },
        advSearch() {
            var condition = this.searchCriteriaBuilderInstance.getConditionStr();
            if (condition === '' || condition === 'INVALID') {
                this.selectedTab = 0;
                return;
            }
            var pattern = {
                contentTypeIDs: this.userSearch.contentTypeIDs.join(','),
                searchPattern: condition,
                groupPattern: this.groupModalInstance.getGroupStr(),
                sortPattern: this.sortModalInstance.getSortStr(),
                searchAllVersions: this.userSearch.searchAllVersions,
            };
            this.modalInstance.progress(true);
            var route = '/search/';
            route += `${SearchType.AdvancedSearch}/${encodeURIComponent(
                JSON.stringify(pattern)
            )}`;
            this.$router.push(route);
            this.modalInstance.progress(false);
            this.hide();
            this.resetModal();
        },
        show() {
            this.$v.$reset();
            this.selectedTab = 0;
            this.open = true;
            this.$nextTick(() => {
                if (this.shouldLoadContentType) {
                    this.userSearch.contentTypeIDs = this.userContentTypes.map((x) => x.id);
                    this.contentTypesMultiselectInstance.setSelectedItems(this.userSearch.contentTypeIDs);
                } else {
                    this.contentTypesMultiselectInstance.setSelectedItems(
                        this.userSearch.contentTypeIDsIsLastVersion
                    );
                }
            });
        },
        onCancel() {
            this.hide();
            this.resetModal();
        },
        hide() {
            this.open = false;
        },
        resetModal() {
            this.shouldLoadContentType = true;
            this.groupModalInstance.reset();
            this.sortModalInstance.reset();
            if (this.userSearch != new UserSearch()) {
                this.userSearch = new UserSearch();
                this.searchCriteriaBuilderInstance.reset();
            }
        },
        clear() {
            this.resetModal();
            this.userSearch.contentTypeIDs = this.userContentTypes.map((x) => x.id);
            this.contentTypesMultiselectInstance.setSelectedItems(this.userSearch.contentTypeIDs);
        },
        async edit(item) {
            this.shouldLoadContentType = false;
            this.show();
            this.userSearch = new UserSearch(item);
            const contentTypeIDsSelected = this.userSearch.contentTypeIDs;
            this.userSearch.contentTypeIDs = contentTypeIDsSelected;
            this.$nextTick(async () => {
                this.filteredFields = [];
                if (this.contentTypesMultiselectInstance) {
                    this.contentTypesMultiselectInstance.setSelectedItems(
                        contentTypeIDsSelected
                    );
                }
                await this.searchCriteriaBuilderInstance.edit(this.userSearch.conditionStr);
                this.groupModalInstance.setGroupObj(this.userSearch.groupStr);
                this.sortModalInstance.setSortObj(this.userSearch.sortStr);
                this.userSearch.contentTypeIDs = contentTypeIDsSelected;
                this.shouldLoadContentType = true;
            });
        },
    },
};
</script>
